<!--
/**
 * This is the Planungsgeschaeft Grid component: It renders a KtgTable with
 * (filtered) Planungsgeschaefte and capsules all remote details like
 * filtering, paging, sorting querying.
 *
 * You can configure it using the exposed props and v-models.
 * @name PlanungsgeschaeftGrid
 *
 */
-->
<script setup lang="ts">
import KtgTable from '@kp/geofy-vuejs-lib/tg-system/components/KpTable.vue';
import type { KtgTableHeaderProps, KtgTableSortEntry } from '@kp/geofy-vuejs-lib/tg-system/components/KpTableTypes.js';
import StatusPill from '@/components/StatusPill.vue';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGeschaefteCollection } from '@/composables/usePlanungsgeschaefteCollection';
import { RouterLink } from 'vue-router';
import { useBrowserSessionValue } from '@kp/geofy-vuejs-lib/tools/browser_session.js';
import type { PlanningPermissionApplication } from '@/models/PlanningPermissionApplication';

// ------------------ Props and models ---------------------------
const props = withDefaults(
    defineProps<{
        /**
         * needed: we store browser session values. per grid, we need a unique key.
         */
        sessionKey: string;
        /**
         * Initial record limit
         */
        initialLimit?: number;
        /**
         * Increase record limit by n records per click
         */
        increaseLimitBy?: number;
        /**
         * a search query, we call the api with ?query= with that value
         */
        searchQuery?: string;
    }>(),
    {
        initialLimit: 20,
        increaseLimitBy: 20
    }
);

/**
 * reactive filters for the collection
 */
const filters = defineModel<ApiFilter[]>('filters', {
    default: () => []
});
// ---------------------------------------------------------------

const emits = defineEmits<{
    // When the internal collection is loading, 'loading' is emitted.
    (e: 'loading', value: boolean): void;
    // As soon as the server total changes from the remote collection,
    // we emit this info for outer components:
    (e: 'serverTotal', value: number): void;
}>();

// Table properties like paging, sorting, filtering are stored
// in the Browser's Session storage, and restored on component mount:
const actPage = useBrowserSessionValue(`${props.sessionKey}_page`, 1);
const actPageSize = useBrowserSessionValue(`${props.sessionKey}_page_size`, props.initialLimit);
const actTableSort = useBrowserSessionValue(`${props.sessionKey}_table_sort`, [
    { dataProperty: 'id', direction: 'descending' } as KtgTableSortEntry
]);

// We use a reactive GeschaefteCollection for the table component,
// and use the browser session values as defaults:
const {
    models: geschaeftModels,
    query: queryGeschaefte,
    collection: geschaefteCollection,
    tableSort,
    pageSize,
    serverTotal
} = useGeschaefteCollection({
    page: actPage,
    pageSize: actPageSize,
    tableSort: actTableSort,
    collectionsFilter: filters
});

// As soon as the server total changes from the remote collection,
// we emit this info for outer components:
watch(serverTotal, (val) => emits('serverTotal', val));

const tableHeaders = ref<KtgTableHeaderProps[]>([
    {
        title: 'ID',
        dataProperty: 'id',
        sizing: 'min-content',
        alignment: 'left',
        sortable: true
    },
    {
        title: useI18n().t('pg_nummer_kanton'),
        dataProperty: 'boa_bgnumber',
        sortable: true
    },
    {
        title: useI18n().t('pg_nummer_gemeinde'),
        dataProperty: 'dossier_nr_gemeinde',
        sortable: true
    },
    {
        title: useI18n().t('title'),
        dataProperty: 'description',
        sizing: 'min-content',
        sortable: true
    },
    {
        title: useI18n().t('gemeinde'),
        dataProperty: 'stelle',
        sortable: true
    },
    {
        title: useI18n().t('aktueller_status'),
        dataProperty: 'aktueller_status_ebau_value',
        sortable: true
    },
    {
        title: useI18n().t('last_modified'),
        dataProperty: 'last_modified',
        sortable: true
    },
    {
        title: '',
        dataProperty: 'actions',
        sticky: true,
        sortable: false,
        alignment: 'right'
    }
]);

onMounted(async () => {
    await query();
});

watch(() => props.searchQuery, onSearchQueryChange);

async function query() {
    emits('loading', true);
    try {
        await queryGeschaefte({
            // append the search field as query to the request:
            query: props.searchQuery?.trim() || null
        });
    } finally {
        emits('loading', false);
    }
}

function onSearchQueryChange(queryStr: string | undefined) {
    geschaefteCollection.setQueryParam('query', queryStr?.trim() || null);
    query();
}

function getOeffnenButtonText(item: PlanningPermissionApplication) {
    return item.aktueller_status_ebau_value === 'new' || item.aktueller_status_ebau_value === 'demand documents'
        ? useI18n().t('edit')
        : useI18n().t('einsehen');
}
</script>
<template>
    <KtgTable v-model:sort="tableSort" :headers="tableHeaders" :data="geschaeftModels" class="-ml-1rem">
        <template #[`item.description`]="{ item }">
            <div class="titleCol" :title="item.description">{{ item.description }}</div>
        </template>
        <template #[`item.location_street`]="{ item }">
            {{ item.location_street }} {{ item.location_house_number }}
        </template>
        <template #[`item.location_town`]="{ item }">
            {{ item.location_swiss_zip_code }} {{ item.location_town }}
        </template>
        <template #[`item.last_modified`]="{ item }">
            {{ item.last_modified?.formatIf('DD.MM.YYYY HH:mm') }}
        </template>
        <template #[`item.aktueller_status_ebau_value`]="{ item }">
            <StatusPill :status="item.aktueller_status_ebau_value" :text="item.aktueller_status" />
        </template>
        <template #[`item.actions`]="{ item }">
            <RouterLink
                custom
                :to="{ name: 'planungsgeschaeft-edit-dashboard', params: { id: item.id } }"
                v-slot="{ href, navigate }"
            >
                <ktg-table-button :href="href" @click.prevent="navigate()" class="capitalize">{{
                    getOeffnenButtonText(item)
                }}</ktg-table-button>
            </RouterLink>
        </template>
    </KtgTable>
    <div class="mt-1_5rem flex justify-content-end align-items-center">
        <span class="px-1rem">{{ Math.min(pageSize, serverTotal) }} {{ $t('von') }} {{ serverTotal }}</span>
        <ktg-button
            v-if="pageSize < serverTotal"
            appearance="tertiary"
            size="small"
            @click="pageSize = Math.min(pageSize + props.increaseLimitBy, serverTotal)"
            trailing-icon="plus"
        >
            {{ $t('show_n_more', { n: props.increaseLimitBy }) }}
        </ktg-button>
    </div>
</template>

<style scoped lang="scss">
.titleCol {
    max-width: 400px;
    text-overflow: ellipsis;
    overflow: clip;
}
</style>
